import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { CircleAlert } from 'lucide-react'
import { getV1MeRetrieveQueryOptions } from '@/api/client'
import { DataTable, type Column } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  entityLabel,
  failureLabel,
  isSystemErrorsSuccess,
  kindLabel,
  reasonLabel,
  systemErrorsQueryOptions,
  type SystemErrorFailureKind,
  type SystemErrorRow,
} from '@/lib/system-errors'

export const Route = createFileRoute('/_panel/bledy-systemowe')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getV1MeRetrieveQueryOptions())
    if (me.status === 200 && me.data.role !== 'admin' && me.data.role !== 'superadmin') {
      throw redirect({ to: '/' })
    }
  },
  component: SystemErrorsPage,
})

const PAGE_SIZE = 25
const ALL_FAILURES = 'all'

const columns: Column<SystemErrorRow>[] = [
  {
    header: 'Czas zgłoszenia',
    cell: (row) => <time dateTime={row.received_at}>{formatDateTime(row.received_at)}</time>,
  },
  {
    header: 'Uczeń',
    cell: (row) => <span className="font-medium">{row.student_username}</span>,
  },
  { header: 'Zdarzenie', cell: (row) => row.description },
  { header: 'Typ danych', cell: (row) => entityLabel(row.entity_type) },
  { header: 'Zmiana', cell: (row) => kindLabel(row.kind) },
  { header: 'Przyczyna', cell: (row) => reasonLabel(row.reason_code) },
]

function SystemErrorsPage() {
  const [page, setPage] = useState(1)
  const [failureKind, setFailureKind] = useState<SystemErrorFailureKind | typeof ALL_FAILURES>(
    ALL_FAILURES,
  )
  const filters = {
    page,
    page_size: PAGE_SIZE,
    failure_kind: failureKind === ALL_FAILURES ? undefined : failureKind,
  }
  const query = useQuery(systemErrorsQueryOptions(filters))
  const data = isSystemErrorsSuccess(query.data) ? query.data.data : undefined

  return (
    <>
      <PageHeader title="Błędy systemowe" />
      <div className="p-6">
        {query.data && query.data.status >= 400 ? (
          <div className="flex max-w-2xl gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
            <CircleAlert className="mt-0.5 size-5 shrink-0" />
            <div>
              <p className="font-medium">Nie udało się pobrać błędów systemowych.</p>
              <p className="mt-1 text-amber-900">
                Spróbuj odświeżyć stronę. Jeśli problem się powtórzy, skontaktuj się z
                administratorem systemu.
              </p>
            </div>
          </div>
        ) : (
          <DataTable
            columns={columns}
            rows={data?.results ?? []}
            rowKey={(row) => row.id}
            count={data?.count ?? 0}
            page={page}
            onPageChange={setPage}
            pageSize={PAGE_SIZE}
            search=""
            onSearchChange={() => undefined}
            hideSearch
            filters={
              <Select
                value={failureKind}
                onValueChange={(value) => {
                  setFailureKind(value as SystemErrorFailureKind | typeof ALL_FAILURES)
                  setPage(1)
                }}
              >
                <SelectTrigger size="sm" className="w-52">
                  <SelectValue placeholder="Wszystkie błędy" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FAILURES}>Wszystkie błędy</SelectItem>
                  <SelectItem value="permanent">{failureLabel('permanent')}</SelectItem>
                  <SelectItem value="retry_exhausted">{failureLabel('retry_exhausted')}</SelectItem>
                </SelectContent>
              </Select>
            }
            isLoading={query.isLoading}
            emptyMessage="Brak zarejestrowanych błędów systemowych."
            countNoun={['zdarzenie', 'zdarzeń']}
          />
        )}
      </div>
    </>
  )
}

function formatDateTime(value: string): string {
  return new Intl.DateTimeFormat('pl-PL', { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  )
}
